import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Pencil, Trash2, Tag, Loader2, X, Layers, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminMainCategoriesManager() {
  const [groups, setGroups] = useState([]);
  const [squareCats, setSquareCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, sinon objet group
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, squareRes] = await Promise.all([
        axios.get(`${API}/main-categories`),
        axios.get(`${API}/main-categories/square-categories-available`, { withCredentials: true }),
      ]);
      // Filter out "_others" (auto-généré)
      setGroups((groupsRes.data.groups || []).filter(g => !g.is_auto));
      setSquareCats(squareRes.data.categories || []);
    } catch (err) {
      toast.error("Erreur de chargement des groupes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const startCreate = () => {
    setEditing({
      label: '',
      description: '',
      emoji: '',
      square_categories: [],
      display_order: groups.length,
      is_active: true,
    });
    setEditorOpen(true);
  };

  const startEdit = (g) => {
    setEditing({ ...g });
    setEditorOpen(true);
  };

  const closeEditor = () => {
    setEditing(null);
    setEditorOpen(false);
  };

  const saveGroup = async () => {
    if (!editing.label || editing.label.trim().length < 2) {
      toast.error("Le libellé doit faire au moins 2 caractères");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: editing.label.trim(),
        description: (editing.description || '').trim(),
        emoji: (editing.emoji || '').trim(),
        square_categories: editing.square_categories || [],
        display_order: editing.display_order ?? 0,
        is_active: editing.is_active !== false,
      };
      if (editing.category_id) {
        await axios.put(`${API}/main-categories/${editing.category_id}`, payload, { withCredentials: true });
        toast.success(`Groupe "${payload.label}" mis à jour`);
      } else {
        await axios.post(`${API}/main-categories`, payload, { withCredentials: true });
        toast.success(`Groupe "${payload.label}" créé`);
      }
      closeEditor();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Supprimer le groupe "${g.label}" ?\nLes produits restent visibles, ils retomberont dans "Autres".`)) return;
    try {
      await axios.delete(`${API}/main-categories/${g.category_id}`, { withCredentials: true });
      toast.success(`Groupe "${g.label}" supprimé`);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de suppression");
    }
  };

  const toggleSquareCat = (sqName) => {
    setEditing(prev => {
      const list = prev.square_categories || [];
      const has = list.includes(sqName);
      return {
        ...prev,
        square_categories: has ? list.filter(s => s !== sqName) : [...list, sqName],
      };
    });
  };

  return (
    <Card className="bg-[#261B3B] border-white/10" data-testid="admin-main-categories">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="font-serif text-xl text-white flex items-center gap-2">
          <Layers className="w-5 h-5 text-[#D926A9]" />
          Groupes de catégories de la boutique
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/admin/main-categories/duplicates`, { withCredentials: true });
                const dupGroups = res.data?.groups || [];
                if (dupGroups.length === 0) {
                  toast.success("Aucun doublon détecté ✨");
                  return;
                }
                const preview = dupGroups.map(g => `• ${g.label} → ${g.duplicates.length} doublon(s)`).join('\n');
                const ok = window.confirm(
                  `${dupGroups.length} groupe(s) en doublon détecté(s) :\n\n${preview}\n\nLes doublons seront supprimés et leurs catégories Square fusionnées dans le canonique. Confirmer ?`
                );
                if (!ok) return;
                const dr = await axios.post(`${API}/admin/main-categories/dedupe`, {}, { withCredentials: true });
                toast.success(`${dr.data.deleted_count} doublon(s) supprimé(s) ✨`);
                await fetchData();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erreur lors de la détection des doublons");
              }
            }}
            className="border-orange-500/30 text-orange-300 hover:bg-orange-500/10"
            data-testid="main-cats-dedupe-btn"
            title="Détecter et supprimer les groupes en doublon (même libellé)"
          >
            🧹 Dédoublonner
          </Button>
          <Button size="sm" onClick={startCreate} className="gradient-primary gap-2" data-testid="main-cat-create-btn">
            <Plus className="w-4 h-4" /> Nouveau groupe
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-[#B59ED1] mb-4">
          Les groupes sont les onglets principaux de la boutique. Chaque groupe peut contenir plusieurs
          catégories Square — quand un client clique sur le groupe, tous les produits des catégories
          incluses s'affichent et il peut affiner via les sous-filtres.
        </p>

        {loading ? (
          <div className="text-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-[#D926A9] mx-auto" />
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map(g => (
              <div
                key={g.category_id}
                className="p-4 bg-[#1B132B] rounded-lg border border-white/5 flex items-start justify-between gap-3"
                data-testid={`main-cat-row-${g.category_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    {g.emoji && <span className="text-xl">{g.emoji}</span>}
                    <h4 className="font-semibold text-white">{g.label}</h4>
                    <Badge variant="outline" className="border-[#D926A9]/40 text-[#D926A9] text-xs">
                      {g.product_count || 0} produits
                    </Badge>
                    {g.is_active === false && <Badge variant="outline" className="border-orange-500/40 text-orange-400 text-xs">Inactif</Badge>}
                  </div>
                  {g.description && <p className="text-xs text-[#B59ED1] mb-2 line-clamp-2">{g.description}</p>}
                  <div className="flex flex-wrap gap-1.5">
                    {(g.square_categories || []).map(sq => (
                      <span key={sq} className="text-[10px] bg-white/5 text-[#E2D5F8] px-2 py-0.5 rounded-full border border-white/10">
                        {sq}
                      </span>
                    ))}
                    {(!g.square_categories || g.square_categories.length === 0) && (
                      <span className="text-[10px] italic text-orange-400">Aucune catégorie Square assignée</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(g)} className="h-7 text-[#00E5FF]" data-testid={`main-cat-edit-${g.category_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteGroup(g)} className="h-7 text-red-400 hover:text-red-300" data-testid={`main-cat-del-${g.category_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {groups.length === 0 && (
              <div className="text-center py-8 text-[#B59ED1] text-sm">
                Aucun groupe. Clique sur "Nouveau groupe" pour en créer un.
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Éditeur (création + modification) */}
      <Dialog open={editorOpen} onOpenChange={(v) => !saving && (v ? setEditorOpen(true) : closeEditor())}>
        <DialogContent className="bg-[#261B3B] border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="main-cat-editor">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-white">
              {editing?.category_id ? `Modifier "${editing?.label}"` : 'Nouveau groupe'}
            </DialogTitle>
            <DialogDescription className="text-[#B59ED1] text-sm">
              Coche les catégories Square qui composent ce groupe.
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-[80px_1fr] gap-3">
                <div>
                  <Label className="text-[#E2D5F8] text-xs">Emoji</Label>
                  <Input
                    value={editing.emoji}
                    onChange={(e) => setEditing({ ...editing, emoji: e.target.value })}
                    placeholder="💎"
                    className="bg-[#1B132B] border-white/10 text-white text-center text-xl"
                    maxLength={4}
                    data-testid="main-cat-emoji"
                  />
                </div>
                <div>
                  <Label className="text-[#E2D5F8] text-xs">Libellé du groupe *</Label>
                  <Input
                    value={editing.label}
                    onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                    placeholder="Lithothérapie"
                    className="bg-[#1B132B] border-white/10 text-white"
                    data-testid="main-cat-label"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#E2D5F8] text-xs">Description (affichée en tooltip)</Label>
                <Textarea
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Pierres, cristaux et minéraux pour leurs vertus énergétiques."
                  rows={2}
                  className="bg-[#1B132B] border-white/10 text-white"
                  data-testid="main-cat-description"
                />
              </div>

              <div>
                <Label className="text-[#E2D5F8] text-xs mb-2 block">
                  Catégories Square à inclure ({editing.square_categories?.length || 0} sélectionnées)
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto p-3 bg-[#1B132B] rounded-lg border border-white/5">
                  {squareCats.length === 0 && <p className="text-xs text-[#B59ED1] col-span-2 text-center py-4">Aucune catégorie Square trouvée (synchronise d'abord la boutique).</p>}
                  {squareCats.map(sq => {
                    const checked = editing.square_categories?.includes(sq.name);
                    return (
                      <label
                        key={sq.name}
                        className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer transition-colors text-xs ${checked ? 'bg-[#D926A9]/15 border border-[#D926A9]/40' : 'bg-white/3 border border-transparent hover:border-white/10'}`}
                        data-testid={`main-cat-sq-toggle-${sq.name}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSquareCat(sq.name)}
                          className="rounded border-white/20 accent-[#D926A9]"
                        />
                        <span className="text-white flex-1 truncate" title={sq.name}>{sq.name}</span>
                        <Badge variant="outline" className="border-white/10 text-[#B59ED1] text-[10px] shrink-0">{sq.count}</Badge>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#E2D5F8] text-xs">Ordre d'affichage</Label>
                  <Input
                    type="number"
                    value={editing.display_order}
                    onChange={(e) => setEditing({ ...editing, display_order: parseInt(e.target.value) || 0 })}
                    className="bg-[#1B132B] border-white/10 text-white"
                    data-testid="main-cat-order"
                  />
                </div>
                <div className="flex items-end gap-2">
                  <label className="flex items-center gap-2 text-sm text-[#E2D5F8] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editing.is_active !== false}
                      onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                      className="rounded border-white/20 accent-[#D926A9]"
                      data-testid="main-cat-active"
                    />
                    Actif
                  </label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={closeEditor} disabled={saving} className="text-[#B59ED1]">
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={saveGroup} disabled={saving} className="gradient-primary" data-testid="main-cat-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
